import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  children,
  className,
  size = 'md',
}: EmptyStateProps) {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-16',
    lg: 'py-24',
  };

  const iconSizeClasses = {
    sm: 'w-12 h-12 rounded-xl',
    md: 'w-16 h-16 rounded-2xl',
    lg: 'w-20 h-20 rounded-2xl',
  };

  const iconInnerSizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };

  return (
    <div className={cn(
      'flex flex-col items-center justify-center px-4 text-center animate-fade-in',
      sizeClasses[size],
      className
    )}>
      <div className={cn(
        'bg-primary/10 flex items-center justify-center mb-6',
        iconSizeClasses[size]
      )}>
        <Icon className={cn('text-primary', iconInnerSizeClasses[size])} />
      </div>
      <h3 className={cn(
        'font-semibold mb-2 text-foreground',
        size === 'sm' ? 'text-lg' : size === 'md' ? 'text-xl' : 'text-2xl'
      )}>
        {title}
      </h3>
      <p className={cn(
        'text-muted-foreground mb-6',
        size === 'sm' ? 'text-sm max-w-sm' : 'max-w-md'
      )}>
        {description}
      </p>
      
      {action && (
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {action}
        </div>
      )}
      
      {children}
    </div>
  );
}
